import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Send, Loader2, Wallet, AlertCircle } from 'lucide-react';
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from "@/components/ui/alert";

interface NotificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loyaltyProgram: {
    id: string;
    name: string;
    business_id: string;
  };
  business: {
    id: string;
    name: string;
  };
}

const SERVER_URL = import.meta.env.DEV
  ? 'https://pass-dev.taginn.app'
  : 'https://pass.taginn.app';

const NotificationModal: React.FC<NotificationModalProps> = ({
  open,
  onOpenChange,
  loyaltyProgram,
  business
}) => {
  // Manual notification state
  const [notificationHeader, setNotificationHeader] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [googlePassCount, setGooglePassCount] = useState(0);
  const [rateLimit, setRateLimit] = useState<any>(null);

  useEffect(() => {
    if (open) {
      fetchPassHolderCounts();
      checkRateLimit();
      setNotificationHeader(`New from ${business.name}`);
      setNotificationMessage('');
    }
  }, [open, loyaltyProgram.id]);

  const fetchPassHolderCounts = async () => {
    try {
      const { data: passes, error } = await supabase
        .from('passes')
        .select('pass_type_identifier')
        .eq('loyalty_program_id', loyaltyProgram.id);

      if (error) throw error;

      const googlePasses = passes?.filter(p =>
        p.pass_type_identifier === 'pass.com.tag-innovation.google-wallet'
      ) || [];

      setGooglePassCount(googlePasses.length);
    } catch (error) {
      console.error('Error fetching pass counts:', error);
    }
  };

  const checkRateLimit = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/notifications/check-rate-limit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loyalty_program_id: loyaltyProgram.id,
          platform: 'google'
        })
      });

      const data = await response.json();
      setRateLimit(data);
    } catch (error) {
      console.error('Error checking rate limit:', error);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationHeader.trim()) {
      toast.error("Header required", {
        description: "Please enter a notification header"
      });
      return;
    }

    if (!notificationMessage.trim()) {
      toast.error("Message required", {
        description: "Please enter a notification message"
      });
      return;
    }

    if (notificationHeader.length > 50) {
      toast.error("Header too long", {
        description: "Header must be 50 characters or less"
      });
      return;
    }

    if (notificationMessage.length > 200) {
      toast.error("Message too long", {
        description: "Message must be 200 characters or less"
      });
      return;
    }

    if (googlePassCount === 0) {
      toast.error("No recipients", {
        description: "This loyalty program has no Google Wallet pass holders yet"
      });
      return;
    }

    // Check rate limit
    if (rateLimit && !rateLimit.allowed) {
      toast.error("Rate limit exceeded", {
        description: `Notification limit reached. Try again after ${new Date(rateLimit.resets_at).toLocaleTimeString()}`
      });
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch(`${SERVER_URL}/notifications/send-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          loyalty_program_id: loyaltyProgram.id,
          message: notificationMessage.trim(),
          header: notificationHeader.trim(),
          platform: 'google'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Failed to send notification');
      }

      const totalSent = data.google_sent;
      const totalFailed = data.google_failed;

      toast.success("Notification sent", {
        description: `Sent to ${totalSent} Google Wallet pass holder${totalSent !== 1 ? 's' : ''}${totalFailed > 0 ? ` (${totalFailed} failed)` : ''}`
      });

      setNotificationHeader(`New from ${business.name}`);
      setNotificationMessage('');
      await checkRateLimit(); // Refresh rate limit
    } catch (error: any) {
      console.error('Error sending notification:', error);
      toast.error("Failed to send notification", {
        description: error.message || "An error occurred"
      });
    } finally {
      setIsSending(false);
    }
  };

  const headerCharacterLimit = 50;
  const messageCharacterLimit = 200;
  const remainingHeaderChars = headerCharacterLimit - notificationHeader.length;
  const remainingMessageChars = messageCharacterLimit - notificationMessage.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            Send Notification
          </DialogTitle>
          <DialogDescription>
            Send custom notifications to Google Wallet pass holders
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Header Input */}
          <div className="space-y-2">
            <Label htmlFor="header">Notification Header</Label>
            <Input
              id="header"
              value={notificationHeader}
              onChange={(e) => setNotificationHeader(e.target.value)}
              placeholder={`New from ${business.name}`}
              maxLength={headerCharacterLimit}
            />
            <div className="flex justify-end text-sm">
              <p className={remainingHeaderChars < 10 ? "text-orange-500" : "text-muted-foreground"}>
                {remainingHeaderChars} characters remaining
              </p>
            </div>
          </div>

          {/* Message Input */}
          <div className="space-y-2">
            <Label htmlFor="message">Notification Message</Label>
            <Textarea
              id="message"
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder={`Special offers available today!`}
              maxLength={messageCharacterLimit}
              rows={3}
              className="resize-none"
            />
            <div className="flex justify-end text-sm">
              <p className={remainingMessageChars < 20 ? "text-orange-500" : "text-muted-foreground"}>
                {remainingMessageChars} characters remaining
              </p>
            </div>
          </div>

          {/* Google Wallet Pass Count */}
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium">Google Wallet Pass Holders</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {googlePassCount}
            </span>
          </div>

          {/* Rate Limit Warning */}
          {rateLimit && (
            <Alert className={rateLimit.remaining < 1 ? "border-orange-200 bg-orange-50" : ""}>
              <AlertDescription className="text-sm">
                <span className="font-medium">Rate Limit: </span>
                {rateLimit.remaining} of 3 notifications remaining today
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            disabled={isSending || !notificationMessage.trim() || !notificationHeader.trim()}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Notification
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
